import React, { useEffect, useMemo, useState } from "react";
import { CreditCard, Info, Loader2, Lock, ShieldCheck, Tag } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApiService } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { getPaymentProviderMeta } from "../../utils/paymentProviders";

const CARD_LOGOS = [
  { name: "Mastercard", src: "/images/payment-providers/mastercard.svg", className: "max-h-8" },
  { name: "Visa", src: "/images/payment-providers/visa.svg", className: "max-h-7" },
  { name: "Troy", src: "/images/payment-providers/troy.svg", className: "max-h-8" },
  { name: "BKM Express", src: "/images/payment-providers/bkm-express.png", className: "max-h-8" },
  { name: "Discover", src: "/images/payment-providers/discover.svg", className: "max-h-7" },
  { name: "American Express", src: "/images/payment-providers/american-express.svg", className: "max-h-8" },
];

type FormState = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

type FormErrors = Partial<Record<keyof FormState, string>>;

function validateForm(form: FormState): FormErrors {
  const errors: FormErrors = {};
  if (!form.firstName.trim()) errors.firstName = "Ad zorunludur.";
  if (!form.lastName.trim()) errors.lastName = "Soyad zorunludur.";
  if (!form.email.trim()) {
    errors.email = "E-posta zorunludur.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = "Geçerli bir e-posta adresi girin.";
  }
  if (!form.phone.trim()) {
    errors.phone = "Telefon zorunludur.";
  } else if (!/^(\+90|0)?\d{10}$/.test(form.phone.replace(/\s/g, ""))) {
    errors.phone = "Geçerli bir telefon numarası girin.";
  }
  return errors;
}

function calcDiscountPct(pkg: any, dailyPrice: number): number {
  if (!dailyPrice || !pkg.unit || !pkg.value) return 0;
  const days = pkg.unit === "day" ? pkg.value : pkg.unit === "month" ? pkg.value * 30 : 0;
  if (days <= 1) return 0;
  const expected = days * dailyPrice;
  const discount = Math.round(((expected - pkg.price) / expected) * 100);
  return discount > 0 ? discount : 0;
}

export default function PaymentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo") || "/dashboard";
  const { user } = useAuth();

  const [catalog, setCatalog] = useState<any>(null);
  const [packageId, setPackageId] = useState("");
  const [provider, setProvider] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState<FormState>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof FormState, boolean>>>({});
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [legalError, setLegalError] = useState("");

  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.gsmNumber || "",
      });
    }
  }, [user]);

  useEffect(() => {
    ApiService.getPaymentCatalog()
      .then((data) => {
        setCatalog(data);
        setPackageId(data.packages?.[0]?.id || "");
        setProvider(data.providers?.[0]?.id || "");
      })
      .catch((err) => setError(err?.message || "Ödeme seçenekleri yüklenemedi."))
      .finally(() => setLoading(false));
  }, []);

  const selectedPackage = useMemo(
    () => catalog?.packages?.find((item: any) => item.id === packageId),
    [catalog, packageId]
  );

  const dailyPrice = useMemo(() => {
    const day1 = catalog?.packages?.find((p: any) => p.id === "day_1");
    return day1?.price || 0;
  }, [catalog]);

  const selectedProviderMeta = useMemo(
    () => (provider ? getPaymentProviderMeta(provider) : null),
    [provider]
  );

  const handleField = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (touched[field]) {
      const errors = validateForm({ ...form, [field]: value });
      setFormErrors((prev) => ({ ...prev, [field]: errors[field] }));
    }
  };

  const handleBlur = (field: keyof FormState) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errors = validateForm(form);
    setFormErrors((prev) => ({ ...prev, [field]: errors[field] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ firstName: true, lastName: true, email: true, phone: true });
    const errors = validateForm(form);
    setFormErrors(errors);

    if (!privacyAccepted || !termsAccepted) {
      setLegalError("Devam etmek için tüm sözleşmeleri kabul etmeniz gerekmektedir.");
      return;
    }
    setLegalError("");
    if (Object.keys(errors).length > 0) return;

    setError("");
    setSubmitting(true);
    try {
      const quoteResult = await ApiService.getPaymentQuote({
        packageId,
        provider,
        installment: 1,
      });

      sessionStorage.setItem("paymentReturnTo", returnTo);
      const result = await ApiService.startPaymentCheckout({
        packageId,
        provider,
        quoteToken: quoteResult.quoteToken,
      });

      if (result.paymentPageUrl) {
        window.location.assign(result.paymentPageUrl);
        return;
      }
      throw new Error("Ödeme sayfası bağlantısı alınamadı.");
    } catch (err: any) {
      setError(err?.message || "Ödeme başlatılamadı.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (!catalog?.paymentEnabled || !catalog?.packages?.length || !catalog?.providers?.length) {
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center">
        <Lock className="mx-auto mb-3 h-10 w-10 text-amber-600" />
        <h1 className="text-xl font-semibold text-slate-900">Ödeme Sistemi Şu Anda Kullanılamıyor</h1>
        <p className="mt-2 text-sm text-slate-600">Lütfen sistem yöneticinizle iletişime geçin.</p>
        <button
          onClick={() => navigate("/dashboard")}
          className="mt-5 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
        >
          Ana Sayfaya Dön
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50 py-10 px-4 sm:px-8">
      <div className="mx-auto max-w-5xl">

        {/* Başlık */}
        <div className="mb-8 flex items-center gap-3">
          <div className="rounded-2xl bg-violet-100 p-2.5">
            <ShieldCheck className="h-6 w-6 text-violet-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">YES.Tools Ücretli Modüller</h1>
            <p className="text-sm text-slate-500">Bir paket seçerek ücretli modüllerin tamamına erişin.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-6 lg:grid-cols-[1fr_340px]">

          {/* Sol: Paket Seçimi + Kişisel Bilgiler */}
          <div className="flex flex-col gap-6">

            {/* Erişim Paketi */}
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-base font-bold text-slate-900">Erişim Paketi</h2>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {catalog.packages.map((item: any) => {
                  const discount = calcDiscountPct(item, dailyPrice);
                  const selected = packageId === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setPackageId(item.id)}
                      className={`relative rounded-2xl border p-4 text-left transition ${
                        selected
                          ? "border-violet-500 bg-violet-50 ring-2 ring-violet-100"
                          : "border-slate-200 hover:border-violet-300 hover:bg-slate-50"
                      }`}
                    >
                      {discount >= 5 && (
                        <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2 py-0.5 text-[11px] font-bold text-white">
                          <Tag className="h-2.5 w-2.5" />
                          %{discount} İndirim
                        </span>
                      )}
                      <div className="pr-16 text-sm font-semibold text-slate-800">{item.label}</div>
                      <div className="mt-1 text-lg font-bold text-violet-700">
                        {Number(item.price).toLocaleString("tr-TR", {
                          style: "currency",
                          currency: "TRY",
                        })}
                      </div>
                      {discount >= 5 && dailyPrice > 0 && (
                        <div className="mt-0.5 text-[11px] text-slate-400 line-through">
                          {Number(
                            (item.unit === "day" ? item.value : item.value * 30) * dailyPrice
                          ).toLocaleString("tr-TR", { style: "currency", currency: "TRY" })}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Kişisel Bilgiler */}
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="mb-5 text-base font-bold text-slate-900">Kişisel Bilgiler</h2>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">Ad</label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={handleField("firstName")}
                    onBlur={handleBlur("firstName")}
                    placeholder="Adınız"
                    className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                      formErrors.firstName ? "border-red-400 bg-red-50" : "border-slate-200 bg-slate-50"
                    }`}
                  />
                  {formErrors.firstName && <p className="mt-1 text-xs text-red-500">{formErrors.firstName}</p>}
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">Soyad</label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={handleField("lastName")}
                    onBlur={handleBlur("lastName")}
                    placeholder="Soyadınız"
                    className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                      formErrors.lastName ? "border-red-400 bg-red-50" : "border-slate-200 bg-slate-50"
                    }`}
                  />
                  {formErrors.lastName && <p className="mt-1 text-xs text-red-500">{formErrors.lastName}</p>}
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">Telefon</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={handleField("phone")}
                    onBlur={handleBlur("phone")}
                    placeholder="05XXXXXXXXX"
                    className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                      formErrors.phone ? "border-red-400 bg-red-50" : "border-slate-200 bg-slate-50"
                    }`}
                  />
                  {formErrors.phone && <p className="mt-1 text-xs text-red-500">{formErrors.phone}</p>}
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">E-Posta</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={handleField("email")}
                    onBlur={handleBlur("email")}
                    placeholder="ornek@eposta.com"
                    className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                      formErrors.email ? "border-red-400 bg-red-50" : "border-slate-200 bg-slate-50"
                    }`}
                  />
                  {formErrors.email && <p className="mt-1 text-xs text-red-500">{formErrors.email}</p>}
                </div>
              </div>

              <div className="mt-5 rounded-2xl border border-violet-100 bg-violet-50/60 px-4 py-3.5">
                <div className="flex items-start gap-3">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-violet-500" />
                  <p className="text-xs leading-relaxed text-slate-600">
                    Taksit seçenekleri, kart bilgisi ve ödeme detayları güvenli ödeme sayfasında gösterilecektir.
                    Kart bilgileriniz YES.Tools sunucularında saklanmaz.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sağ: Sağlayıcı + Özet + CTA */}
          <div className="flex flex-col gap-4 lg:sticky lg:top-6 lg:self-start">

            {/* Ödeme Sağlayıcısı */}
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="mb-4 text-sm font-bold text-slate-900">Ödeme Sağlayıcısı</h2>
              <div className="grid grid-cols-2 gap-2.5">
                {catalog.providers.map((item: any) => {
                  const meta = getPaymentProviderMeta(item.id);
                  const selected = provider === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setProvider(item.id)}
                      className={`flex flex-col items-center justify-center gap-2 rounded-2xl border px-3 py-3.5 transition ${
                        selected
                          ? "border-violet-400 bg-violet-50 ring-2 ring-violet-100"
                          : "border-slate-200 bg-white hover:border-violet-300 hover:bg-slate-50"
                      }`}
                    >
                      <img
                        src={meta.logo}
                        alt={meta.label}
                        className={`${meta.logoClassName} max-w-[90px] object-contain`}
                      />
                      <span className={`text-[11px] font-semibold ${selected ? "text-violet-700" : "text-slate-500"}`}>
                        ile Öde
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Ödeme Özeti + Sözleşmeler + CTA */}
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-sm font-bold text-slate-900">Ödeme Özeti</h2>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Paket</span>
                  <span className="font-medium text-slate-900">{selectedPackage?.label || "—"}</span>
                </div>
                <div className="border-t border-slate-100 pt-2.5">
                  <div className="flex items-baseline justify-between">
                    <span className="font-semibold text-slate-900">Toplam</span>
                    <span className="text-2xl font-bold text-violet-700">
                      {Number(selectedPackage?.price || 0).toLocaleString("tr-TR", {
                        style: "currency",
                        currency: "TRY",
                      })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Sözleşmeler */}
              <div className="mt-5 space-y-3 border-t border-slate-100 pt-5">
                <label className="flex cursor-pointer items-start gap-2.5 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={privacyAccepted}
                    onChange={(e) => { setPrivacyAccepted(e.target.checked); setLegalError(""); }}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded accent-violet-600"
                  />
                  <span className="leading-relaxed">
                    <a href="https://yeniegitimsistemi.net/gizlilik-politikasi" target="_blank" rel="noopener noreferrer" className="font-semibold text-violet-700 underline underline-offset-2">Gizlilik Politikası</a>
                    {" "}ve{" "}
                    <a href="https://yeniegitimsistemi.net/kullanici-sozlesmesi" target="_blank" rel="noopener noreferrer" className="font-semibold text-violet-700 underline underline-offset-2">Kullanıcı Sözleşmesi</a>
                    {" "}metinlerini okudum ve kabul ediyorum.
                  </span>
                </label>

                <label className="flex cursor-pointer items-start gap-2.5 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => { setTermsAccepted(e.target.checked); setLegalError(""); }}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded accent-violet-600"
                  />
                  <span className="leading-relaxed">
                    <a href="https://yeniegitimsistemi.net/mesafeli-satis-sozlesmesi" target="_blank" rel="noopener noreferrer" className="font-semibold text-violet-700 underline underline-offset-2">Mesafeli Satış Sözleşmesi</a>
                    {" "}ile{" "}
                    <a href="https://yeniegitimsistemi.net/islem-rehberi-teslimat-iptal-iade-kosullari" target="_blank" rel="noopener noreferrer" className="font-semibold text-violet-700 underline underline-offset-2">İşlem Rehberi, Teslimat, İptal ve İade Koşulları</a>
                    {" "}metinlerini okudum ve kabul ediyorum.
                  </span>
                </label>

                {legalError && <p className="text-xs text-red-500">{legalError}</p>}
              </div>

              {error && (
                <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={!packageId || !provider || submitting}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-3.5 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:opacity-50"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                {submitting ? "Yönlendiriliyor..." : "Güvenli Ödemeye Geç"}
              </button>

              {/* Güvenli ödeme notu */}
              {selectedProviderMeta && (
                <p className="mt-3 flex flex-wrap items-center justify-center gap-1.5 text-center text-xs leading-relaxed text-slate-400">
                  Ödeme işlemleri
                  <img
                    src={selectedProviderMeta.secureLogo || selectedProviderMeta.logo}
                    alt={selectedProviderMeta.label}
                    className="inline-block h-4 object-contain"
                  />
                  altyapısı ile güvenli şekilde gerçekleştirilir.
                </p>
              )}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="mt-10 border-t border-slate-200 pt-8">
          <div className="mb-5 flex flex-wrap items-center justify-center gap-3">
            {catalog.providers.map((item: any) => {
              const meta = getPaymentProviderMeta(item.id);
              const src = meta.secureLogo || meta.logo;
              const cls = meta.secureLogoClassName || meta.logoClassName;
              return (
                <div key={item.id} className="flex h-12 w-36 items-center justify-center rounded-xl border border-slate-100 bg-white px-3 opacity-60 grayscale transition hover:opacity-100 hover:grayscale-0">
                  <img src={src} alt={meta.label} className={`${cls} max-w-full object-contain`} />
                </div>
              );
            })}
          </div>

          <div className="mb-6 flex flex-wrap items-center justify-center gap-3">
            {CARD_LOGOS.map((card) => (
              <div key={card.name} className="flex h-10 w-16 items-center justify-center rounded-lg border border-slate-100 bg-white px-2 opacity-60 grayscale transition hover:opacity-100 hover:grayscale-0">
                <img src={card.src} alt={card.name} className={`${card.className} max-w-full object-contain`} />
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-slate-400">
            © {new Date().getFullYear()} YES.Tools. Tüm hakları saklıdır.
          </p>
        </div>
      </div>
    </div>
  );
}
